import { action, observable, makeObservable } from 'mobx'
import { types as t, flow } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Mapping, { IMappingSnapshot } from './mapping'
import * as fn from '../../utils/fn'
import ServerFunctionsLog from './ServerFunctionsLog'
import secretsActions from './secrets-actions'
import fetch from '../../utils/fetch'

const secureKeys = ['context', 'js']
const PROVIDER_TYPE = 'Function'

const FnConfig = t
  .model('FN', {
    type: t.optional(t.string, PROVIDER_TYPE),
    lang: t.optional(t.enumeration('lang', Object.keys(fn.languages)), 'js'),
    js: t.maybeNull(t.string),
    inputs: t.maybeNull(t.late(() => Mapping)),
    context: t.maybeNull(t.late(() => Mapping))
  })
  .views(self => ({
    get cleanInputs () {
      if (!self.inputs || !self.inputs.children || !self.inputs.children.length) { return [] }
      return (self.inputs && self.inputs.children) || []
    },
  }))
  .views(self => ({
    get provider () {
      return FnProvider
    },
    get code () {
      return fn[self.lang](self.js, self.cleanInputs)
    },
    get mapping () {
      const m = Mapping.create({ type: 'object' }, { ro: true, parentDataSource: self  });
      (m.children as Array<IMappingSnapshot>) = [self.inputs, self.context].filter(Boolean)
      return m
    },
    get icon () {
      return 'code'
    },
    labels () {
      return {
        key: (self as any).id as string,
        label: (self as any).name as string
      }
    }
  }))
  .actions(crudActions)
  .actions(secretsActions)
  .actions((self: any) => ({
    load: flow(function * () {
      self.setIsOperationInProgress(true);
      const cfg = (yield self.fetchSecureConfig((self as any).dbKey)) ?? {}
      self.setIsOperationInProgress(false);
      
      for (const k of secureKeys) {
        self[k] = cfg[k]
      }
      self.js = self.js || ''
      self.context = self.context || { type: 'object' }
    }),
    onClone: flow(function* (newDbKey: string) {
      const data = {
        provider: PROVIDER_TYPE,
        sourceCredentialsDbKey: (self as any).dbKey,
        targetCredentialsDbKey: newDbKey
      };
      yield self.cloneSecretData(data);
    }),
    afterSave: flow(function*(ds, tempDS) {
      const secretFields = tempDS.getSecretFields(tempDS.context.children)?.filter(f => f.value);
      tempDS.clearSecretFields(tempDS.context.children);
      const data = {
        provider: PROVIDER_TYPE,
        dbKey: ds.dbKey,
        secrets: {
          fields: secretFields,
          config: secureKeys.reduce((o, k) => ({ ...o, [k]: tempDS[k] }), {})
        }
      };
      yield self.saveSecretData(data);
    }),
    afterCreate () {
      self.inputs = self.inputs || { type: 'object' }
    }
  }))

export class FnProvider extends ServerFunctionsLog {
  config

  isLoading = false

  constructor (config) {
    super();
    makeObservable(this, {
      isLoading: observable,
      setIsLoading: action
    })

    this.config = config
  }

  setIsLoading = (b = true) => {
    this.isLoading = b
  }

  run = async (...params) => {
    this.setIsLoading()
    try {
      const res = await fetch(`/api/datasource/${this.config.dbKey}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ params })
      })

      const resp = await res.json()
      if (res.status !== 200) {
        console.error(resp)
        throw new Error(resp.errorMessage)
      }

      return resp.result
    } finally {
      this.setIsLoading(false)
    }
  }
}

export default FnConfig.postProcessSnapshot(snap => {
  const s = { ...snap }
  for (const k of secureKeys) {
    delete s[k]
  }
  return s
})
